<template>
    <div>

        <b-card class="shadow mb-5 ml-md-5 mr-md-5 ml-0 mr-0" v-if="!stub">

            <div class="d-flex w-100 flex-column flex-lg-row align-items-baseline justify-content-center mb-4">

                <b-button variant="link"
                        class="flex-grow-0 d-flex align-items-center text-secondary flex-nowrap mb-3 mb-lg-0"
                        @click="back()">
                    <b-icon icon="chevron-left" class="mr-2"/>
                    <span>Назад</span>
                </b-button>

                <span class="d-flex flex-grow-1 justify-content-center mb-2">
                    <span class="h3 m-0 text-nowrap">
                        <span>{{ kindName }}</span>
                        <span v-if="object.id">&nbsp;№{{ object.id }}</span>
                        <sup><small v-if="!object.id" class="text-secondary ml-2">Новое</small></sup>
                    </span>

                </span>
                <div class="d-flex flex-grow-0 align-items-center mb-2" v-if="object.id && canDownloadPdf">
                    <download-button :url="pdfDownloadUrl" :disabled="false" size-variant="md">
                        <template #title>
                            <span>Выгрузить в pdf</span>
                        </template>
                    </download-button>
                </div>
            </div>


            <div class="d-flex align-items-center mr-1 mb-3" v-if="animal && animal.id">
                <animal-avatar :thumbnail-absolute-path="animal.avatar" :species="animal.species" :size="48"/>
                <div class="d-flex flex-column">
                    <b-link :to="{ name: 'animalDetail', params: { id: animal['id'] } }">
                        <small>{{ animal.speciesName }}, {{ animal.breedName.toString().toLowerCase() }}</small>
                        <b-icon v-if="animal && animal.reviewed" icon="check-circle-fill" variant="success" class="ml-1" v-b-tooltip="getReviewTooltip()"/>
                    </b-link>
                    <span class="text-secondary" :key="eventComponentKey + '-animal'">
                        <small>№{{ animal.regNumber }}</small>
                        <small v-if="animal.shelterRegNumber">, {{ animal.shelterRegNumber }}</small>
                        <small v-if="animal.labelId">, бирка {{ animal.labelId }}</small>
                        <small v-if="animal.chipId">, чип {{ animal.chipId }}</small>
                    </span>
                </div>
            </div>

            <div v-if="needsMunicipality">
                <map-municipality-list :selected-municipality="selectedMunicipality" :editable="isEditable()" @on-municipality-click="onMunicipalityClick"/>
                <div class="clearfix p-2"></div>
            </div>

            <common-event v-if="animal && animal.id" ref="commonEvent"
                          :event-data="objectData"
                          :kind="kind"
                          :selected-municipality="selectedMunicipality"
                          :keeping-contract="keepingContract"
                          :editable="isEditable()"
                          @inline-form-state-changed="onInlineFormChanged($event, 'commonEvent')"
                          class="mb-3"/>

            <component :is="kindComponent"
                       :key="eventComponentKey"
                       ref="attributes"
                       :event-data="objectData"
                       :event-id="object.id"
                       :animal="animal"
                       @init-municipality-selected="initSelectMunicipality"
                       :editable="isEditable()"
                       @files-uploaded="onDocumentsUploaded"
                       @inline-form-state-changed="onInlineFormChanged($event, 'attributes')"/>

            <b-form-row :key="eventComponentKey + '-docs'">
                <form-field class="col-xs-12  col-lg-8 col-xl-6 mt-2 mt-3" :editable="false">
                    <template #value>
                        <gallery :label="'Документы'"
                                 :root-api-url="documentAttachmentsUrl"
                                 :can-update="getAbility() && (!objectId || objectId && animal && !animal.reviewed)"
                                 :max-files="20"
                                 :accepts="documentAccepts"
                                 :upload-immediately="true"
                                 :max-size="20"
                                 :kind="2"
                                 :existing="Boolean(object.id)"
                                 @files-uploaded="onDocumentsUploaded"
                                 ref="documents">
                        </gallery>
                    </template>
                </form-field>

            </b-form-row>

            <object-form-buttons :has-changes="getHasChanges()"
                                 :is-updating="loading"
                                 :key="objectKey + '-hasChanges'"
                                 :object-exists="Boolean(object.id)"
                                 @object-create="onCreateStart"
                                 @object-update="update"
                                 @object-cancel-changes="cancelEventChanges">
                <template #updateButtonName>
                    <span v-if="isTransferEvent">Сохранить и создать новое мероприятие по содержанию</span>
                    <span v-else>Сохранить</span>
                </template>
            </object-form-buttons>

        </b-card>

        <stub :failed-loading="failedLoading" v-else />

        <confirm-leave-modal ref="leaveDialog" :silent-leave="silentLeave" :leave-browser-confirm="hasChanges"/>


    </div>

</template>

<script>
    import {ObjectComponentMixin} from "@/components/Forms/ObjectComponentMixin";
    import CommonEvent from "./CommonEvent";
    import httpClient from "../../../http";
    import api from "../../../api";
    import {eventFactory, EventKind, EventKindLabel, eventNeedsInitialKeeping} from "./event";
    import { mapGetters } from 'vuex';
    import InitialAttributes from "./Clinical/InitialAttributes";
    import {Animal} from "../Animals/animal";
    import AnimalAvatar from "../Animals/AnimalAvatar";
    import CatchingAttributes from "./Catching/CatchingAttributes";
    import DateTimePicker from "../../../components/Forms/DateTimePicker";
    import FormField from "../../../components/Forms/FormField";
    import EventBus from "../../../bus";
    import ConfirmLeaveModal from "../../../components/Forms/ConfirmLeaveModal";
    import ObjectFormButtons from "../../../components/Forms/ObjectFormButtons";
    import Spinner from "../../../components/Spinner";
    import SurveyAttributes from "./Clinical/SurveyAttributes";
    import ExaminationAttributes from "./Clinical/ExaminationAttributes";
    import SterilizationAttributes from "./Clinical/SterilizationAttributes";
    import VaccinationAttributes from "./Clinical/VaccinationAttributes";
    import MarkingAttributes from "./Clinical/MarkingAttributes";
    import KeepingAttributes from "./Keeping/KeepingAttributes";
    import OwnerAttributes from "./Disposal/OwnerAttributes";
    import DeathAttributes from "./Disposal/DeathAttributes";
    import DownloadButton from "../../../components/Files/DownloadButton";
    import EuthanasiaAttributes from "./Disposal/EuthanasiaAttributes";
    import HabitatAttributes from "@/pages/AppPages/Events/Disposal/HabitatAttributes";
    import TransferAttributes from "@/pages/AppPages/Events/Disposal/TransferAttributes.vue";
    import Stub from "@/components/Stub.vue";
    import Gallery from "@/components/Files/Gallery.vue";
    import MapMunicipalityList from "@/components/Map/MapMunicipalityList.vue";

    export default {
        name: "EventDetail",
        components: {
            MapMunicipalityList,
            Gallery,
            Stub,
            Spinner,
            ObjectFormButtons,
            ConfirmLeaveModal, AnimalAvatar, CommonEvent, DateTimePicker,
            FormField, DownloadButton },
        mixins: [ ObjectComponentMixin ],
        props: {
            eventId: { type: Number },
            fromTab: { type: String },  // чтобы потом вернуться на ту же самую вкладку в карточке животного
        },
        data() {
            return {
                kind: null,
                animal: null,
                previousRoute: null,
                eventComponentKey: new Date().getTime(),
                attributesComponentKey: new Date().getTime(),
                inlineChanges: { commonEvent: false, attributes: false },
                bufferedAttachmentIds: [],

                // для выбора контракта для работы по адресу
                selectedMunicipality: null,
                // для выбора контракта, указанного в содержании.
                // !!!Должно использоваться только для клинических мероприятий, если пользователь - не ветстанция-подвед !!!
                // ветстанция сама выбирает свой контракт (он должен подставляться автоматически)
                keepingContract: null,
            }
        },
        computed: {
            ...mapGetters(['ability', 'isVetControl']),
            kindName() {
                const fallback = this.kind ? EventKindLabel[this.kind][0] : null;
                return this.object.id ? this.object.kindName : fallback
            },
            kindComponent() {
                switch(this.kind) {
                    case EventKind.CATCHING:
                        return CatchingAttributes;
                    case EventKind.INITIAL:
                        return InitialAttributes;
                    case EventKind.SURVEY:
                        return SurveyAttributes;
                    case EventKind.CLINICAL:
                        return ExaminationAttributes;
                    case EventKind.STERILIZATION:
                        return SterilizationAttributes;
                    case EventKind.VACCINATION:
                        return VaccinationAttributes;
                    case EventKind.MARKING:
                        return MarkingAttributes;
                    case EventKind.KEEPING:
                        return KeepingAttributes;
                    case EventKind.RETURN_TO_OWNER:
                        return OwnerAttributes;
                    case EventKind.DEATH:
                        return DeathAttributes;
                    case EventKind.EUTHANASIA:
                        return EuthanasiaAttributes;
                    case EventKind.RETURN_TO_HABITAT:
                        return HabitatAttributes;
                    case EventKind.TRANSFER:
                        return TransferAttributes;
                    default:
                        return
                }
            },
            needsMunicipality() {
                const municipalityEventKinds = [EventKind.CATCHING, EventKind.KEEPING]
                return municipalityEventKinds.indexOf(this.kind) >= 0
            },
            canDownloadPdf() {
                return this.kind === EventKind.DEATH || this.kind === EventKind.EUTHANASIA || this.kind === EventKind.SURVEY
            },
            pdfDownloadUrl() {
                const eventUrl = api.events.default.detail(this.object.id);
                return api.animals.pdf.list(eventUrl);
            },
            documentAttachmentsUrl() {
                if (this.object.id) {
                    const eventUrl = api.events.default.detail(this.object.id);
                    return api.events.attachments.list(eventUrl);
                }
                return api.attachments.default.list()
            },
            documentAccepts() { return ['image/jpeg', 'image/tiff', 'application/pdf'] },
            isTransferEvent() {
                return this.kind === EventKind.TRANSFER
            }
        },
        methods: {
            isEditable() {
                if (!this.objectId && this.objectClass) {
                    return this.ability.can('create', this.objectClass.modelName)
                }
                return this.ability.can('update', this.object) && this.animal && !this.animal.reviewed
            },
            getReviewTooltip() {
                return 'Проверено ' + this.animal.reviewDate
            },
            back() {
                this.$router.back();
            },
            async onCreateStart(params) {
                this.create().then(async (routes) => {
                    const route = params.back ? routes.back : routes.next;
                    await this.$router.replace(route);
                });
            },
            gatherData() {
                // собрать и объединить данные со всех вложенных форм
                let formData = {};
                if (this.$refs.commonEvent) {
                    formData = Object.assign(formData, this.$refs.commonEvent.form)
                }
                if (this.$refs.attributes) {
                    let preparedAttributesData = this.$refs.attributes.form;
                    if (typeof this.$refs.attributes['prepareData'] === 'function') {
                        preparedAttributesData = this.$refs.attributes.prepareData();
                    }
                    /// PETS-398
                    if (Object.keys(preparedAttributesData).length > 0){
                        formData = Object.assign(formData, { attributes: preparedAttributesData })
                    } else {
                        delete formData['attributes']
                    }
                    /// PETS-398
                }
                formData.attachments = this.bufferedAttachmentIds;
                formData.animalId = this.form.animalId;
                formData.animal = this.form.animal;
                return formData
            },
            async create() {
                // в некоторых случаях необходимо загружать галерею после создания мероприятия
                this.form = this.gatherData();
                return new Promise((resolve, reject) => {
                    this.createObject()
                        .then((result) => {
                            this.bufferedAttachmentIds = [];
                            resolve(result)
                        }).catch((reason) => {
                            reject(reason)
                        })
                })
            },
            update() {
                this.form = this.gatherData();
                this.updateObject().then((result) => {
                    let animal = new Animal();
                    animal.applyData(result.data['animal_object']);
                    this.animal = animal;
                });
            },
            onInlineFormChanged(param, name) {
                if (this.inlineChanges.hasOwnProperty(name)) {
                    this.inlineChanges[name] = param;
                }
                this.hasChanges = false;
                for (const key in this.inlineChanges) {
                    this.hasChanges = this.hasChanges || this.inlineChanges[key];
                }
            },
            onDocumentsUploaded(uploadedIds) {
                this.bufferedAttachmentIds = [...this.bufferedAttachmentIds, ...uploadedIds.ids]
            },
            resetForm() {
                const vm = this;
                vm.loading = true;
                vm.failedLoading = false;
                this.kind = parseInt(this.$route.params.kind);

                let objectDataPromise = new Promise((resolve, reject) => {
                    resolve({kind: vm.kind})
                })
                if (vm.objectId) {
                    const resource = api.events.default.detail(vm.objectId);
                    objectDataPromise = httpClient.get(resource)
                        .catch((reason) => { vm.failedLoading = true })
                        .then(response => { return response.data })
                }

                objectDataPromise.then(data => {
                    vm.objectData = data;
                    vm.kind = data['kind'];
                    vm.objectClass = eventFactory(vm.kind);
                    vm.apiEntryPoint = api.events;

                    this.initForm();
                    this.initObject().then((data) => {
                        const animalId = vm.object.animalId ? vm.object.animalId : vm.$route.params.animalId;
                        if (animalId) {
                            const animalResource = api.animals.default.detail(animalId);
                            httpClient.get(animalResource)
                                .then(response => {
                                    let animal = new Animal();
                                    animal.applyData(response.data);
                                    vm.animal = animal;
                                    vm.object.animalId = animal.id;
                                    vm.object.animal = animal;
                                    vm.form.animalId = animal.id;
                                    vm.form.animal = animal;
                                    if (eventNeedsInitialKeeping(vm.kind) && !vm.isVetControl) {
                                        vm.keepingContract = animal.lastKeepingData['executor_contract_object'];
                                    }
                                }).finally(() => {
                                    vm.loading = false;
                                    vm.eventComponentKey = new Date().getTime();
                                });
                        }
                    });
                })
            },

            initSelectMunicipality(municipalityObject) {
                this.selectedMunicipality = municipalityObject;
                if (this.$refs.attributes && this.$refs.attributes.hasOwnProperty('toggleSelectedMunicipality')) {
                    this.$refs.attributes.toggleSelectedMunicipality(municipalityObject)
                }
                if (this.$refs.commonEvent && this.$refs.commonEvent.hasOwnProperty('toggleSelectedMunicipality')) {
                    this.$refs.commonEvent.toggleSelectedMunicipality(municipalityObject)
                }
            },

            onMunicipalityClick(municipalityObject) {
                const selectedMunicipalityId = this.selectedMunicipality ? this.selectedMunicipality['id'] : null;
                const municipalityObjectId = municipalityObject ? municipalityObject['id'] : null
                const aboutToChange = selectedMunicipalityId !== municipalityObjectId;
                this.selectedMunicipality = municipalityObject;
                if (this.$refs.attributes.hasOwnProperty('onMunicipalityChanged')) {
                    this.$refs.attributes.onMunicipalityChanged(municipalityObject)
                }
                if (this.$refs.commonEvent.hasOwnProperty('onMunicipalityChanged')) {
                    if (aboutToChange) {
                        this.$refs.commonEvent.onMunicipalityChanged(municipalityObject)
                    }
                }
            },

            cancelEventChanges() {
                this.cancelChanges();
                if (this.$refs.attributes.hasOwnProperty('cancelAttributeChanges')) {
                    this.$refs.attributes.cancelAttributeChanges()
                }
            }

        },
        beforeMount() {
            this.objectId = this.eventId ? this.eventId : this.$route.params.id;
        },
        mounted() {
            this.backRoute = this.previousRoute;
            this.nextRoute = 'eventDetail';
            this.onMount();
            this.resetForm();

        },
        beforeRouteEnter(to, from, next) {
            next(vm => {
                vm.previousRoute = from
            })
        }
    }
</script>

<style scoped>

</style>